import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ArticleCardsSection from './ArticleCardsSection'
import CarouselSection from './CarouselSection'
import ContentImageSection from './ContentImageSection'
import FAQSection from './FAQSection'
import HeroFooterSection from './HeroFooterSection'
import HeroSection from './HeroSection'
import IconsSection from './IconsSection'
import ListImageSection from './ListImageSection'
import TitleSection from './TitleSection'
import LandingPageReferrals from './LandingPageReferrals'
import VideoSection from './VideoSection'

const SECTION_TYPES = {
  ARTICLE_CARDS: 'article_cards',
  CAROUSEL: 'carousel',
  CONTENT_IMAGE: 'content_image',
  DYNAMIC_CONTENT: 'dynamic_content',
  FAQ: 'faq',
  HERO: 'hero',
  HERO_FOOTER: 'hero_footer',
  ICONS_SECTION: 'how_it_works',
  LIST_IMAGE: 'list_image',
  TITLE: 'title',
  VIDEO: 'video',
}

const DYNAMIC_CONTENT_TYPES = {
  REFERRAL_INFO: 'ReferralInfo',
}

// LandingSectionsRenderer: A component that renders different sections on the landing page
// based on the section type.
export default function LandingSectionsRenderer({
  sections = [],
  referralData,
  quickViewModalData,
  articleCardsTranslations,
}) {
  const sectionComponents = useMemo(
    () => ({
      [SECTION_TYPES.ARTICLE_CARDS]: ArticleCardsSection,
      [SECTION_TYPES.CAROUSEL]: CarouselSection,
      [SECTION_TYPES.CONTENT_IMAGE]: ContentImageSection,
      [SECTION_TYPES.FAQ]: FAQSection,
      [SECTION_TYPES.HERO]: HeroSection,
      [SECTION_TYPES.HERO_FOOTER]: HeroFooterSection,
      [SECTION_TYPES.ICONS_SECTION]: IconsSection,
      [SECTION_TYPES.LIST_IMAGE]: ListImageSection,
      [SECTION_TYPES.TITLE]: TitleSection,
      [SECTION_TYPES.VIDEO]: VideoSection,
    }),
    [],
  )

  const dynamicSectionComponents = useMemo(
    () => ({
      [DYNAMIC_CONTENT_TYPES.REFERRAL_INFO]: LandingPageReferrals,
    }),
    [],
  )

  return (
    <div className="landingSections">
      {sections.map((section) => {
        const SectionComponent = section.section_type === SECTION_TYPES.DYNAMIC_CONTENT
          ? dynamicSectionComponents[section.content_type]
          : sectionComponents[section.section_type]

        if (!SectionComponent) {
          console.warn(
            `Unknown section type: ${section.section_type}. Please check the section configuration.`,
          )
          return null
        }

        const props = {
          section,
        }

        // Add quickViewModalData only for CAROUSEL
        if (section.section_type === SECTION_TYPES.CAROUSEL) {
          props.quickViewModalData = quickViewModalData
        }

        // Add articleCardsTranslations only for ARTICLE_CARDS
        if (section.section_type === SECTION_TYPES.ARTICLE_CARDS) {
          props.translations = articleCardsTranslations
        }

        // Add referralData only for DYNAMIC_CONTENT with content_type='ReferralInfo'
        if (
          section.section_type === SECTION_TYPES.DYNAMIC_CONTENT &&
          section.content_type === DYNAMIC_CONTENT_TYPES.REFERRAL_INFO
        ) {
          props.referralData = referralData
        }

        return <SectionComponent key={section.node_id} {...props} />
      })}
    </div>
  )
}

LandingSectionsRenderer.propTypes = {
  articleCardsTranslations: PropTypes.object,
  quickViewModalData: PropTypes.object,
  referralData: PropTypes.object,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      node_id: PropTypes.string.isRequired,
      section_type: PropTypes.oneOf(Object.values(SECTION_TYPES)).isRequired,
    }).isRequired,
  ).isRequired,
}
